import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { BillingPlan } from '@core/billing-types';
import { createSingleEntityStore, memoizeSelector, pipeState } from '@core/ngrx-legacy';

export interface Store {
  activePlan: BillingPlan;
  billingPlans: BillingPlan[];
}

const { actions, selectors, namespace, reducer } = createSingleEntityStore<Store | null>()(
  'BillingPlans',
  {
    customActions: adapter => ({
      fetchSuccess: (state, payload) =>
        pipeState(
          state,
          next => adapter.update(next, { billingPlans: payload.plans }),
          next => adapter.markAsLoaded(next),
        ),
      fetchFailed: (state, payload) => adapter.setError(state, payload.error),
      fetchActivePlan: state => adapter.markAsPending(state),
      fetchActivePlanSuccess: (state, payload: { plan: BillingPlan }) =>
        pipeState(
          state,
          next => adapter.update(next, { activePlan: payload.plan }),
          next => adapter.markAsLoaded(next),
        ),
      fetchActivePlanFailed: (state, payload: { error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error),
    }),
  },
);

const billingPlans = createSelector(selectors.data, state => (state ? state?.billingPlans : null));
const billingPlansPrices = createSelector(billingPlans, plans => {
  if (!plans?.length) return [];
  return plans.filter(plan => plan.prices.length).flatMap(plan => plan.prices);
});
const rangeForQuantity = memoizeSelector((quantity: number | undefined) =>
  createSelector(billingPlansPrices, prices => {
    if (!quantity) return '';
    const price = prices.find(price =>
      price.fteRangeEnd !== null
        ? price.fteRangeStart <= quantity && price.fteRangeEnd >= quantity
        : price.fteRangeStart <= quantity,
    );
    if (!price) throw new Error(`Invalid quantity: ${quantity}`);
    const rangeEnd = price.fteRangeEnd ?? price.fteRangeStart - 1 + 1000;
    return `${price.fteRangeStart}-${rangeEnd}`;
  }),
);
const rangeEndForQuantity = memoizeSelector((quantity: number | undefined) =>
  createSelector(rangeForQuantity(quantity), range => range.split('-')[1] || ''),
);

const BillingPlansSelectors = {
  ...selectors,
  activePlan: createSelector(selectors.data, state => state?.activePlan),
  billingPlans,
  rangeForQuantity,
  rangeEndForQuantity,
};

export {
  actions as BillingPlansActions,
  BillingPlansSelectors,
  namespace as BillingPlansNamespace,
  reducer as BillingPlansReducer,
};
